import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'shared-ui/lib/utils/format-date';
import axios, { isAxiosError } from 'axios';
import { useDeleteServiceEventAttachmentMutation } from '../../../generated/graphql';
import { ConfirmModal } from '../../ConfirmModal';
import { uploadConfig } from '../../../config/upload.config';
import { authConfig } from '../../../config/auth.config';
import { useUploadFile } from '../../../hooks/use-upload-file';
import {
  MAX_ATTACHMENT_FILE_BYTES,
  MAX_SERVICE_EVENT_ATTACHMENTS,
} from 'shared-ui/lib/constants/image-upload-limits';
import { toast } from 'react-toastify';

export type ServiceEventAttachmentRow = {
  id: string;
  name: string;
  url?: string | null;
  createdAt: string;
  canDelete: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  serviceEventId: string;
  attachments: ServiceEventAttachmentRow[];
  hasActiveLicense: boolean;
  canAddAttachments: boolean;
  attachmentsActionsLocked: boolean;
  onRefetch: () => void;
};

export const ServiceEventAttachmentsModal = ({
  isOpen,
  onClose,
  serviceEventId,
  attachments,
  hasActiveLicense,
  canAddAttachments,
  attachmentsActionsLocked,
  onRefetch,
}: Props): JSX.Element | null => {
  const { t: tVehicle } = useTranslation('VehicleShowScene');
  const { t: tShow } = useTranslation('ServiceEventShowScene');
  const [attachmentToDelete, setAttachmentToDelete] = useState<ServiceEventAttachmentRow | null>(
    null
  );
  const [deleteServiceEventAttachment] = useDeleteServiceEventAttachmentMutation();
  const { uploadFile, isImageLoading } = useUploadFile({
    type: 'service-event-attachment',
  });

  const atMaxAttachments = attachments.length >= MAX_SERVICE_EVENT_ATTACHMENTS;
  const showAddAttachmentControls =
    canAddAttachments && !attachmentsActionsLocked;
  const addAttachmentDisabled =
    !canAddAttachments ||
    !hasActiveLicense ||
    isImageLoading ||
    atMaxAttachments ||
    attachmentsActionsLocked;

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (attachments.length >= MAX_SERVICE_EVENT_ATTACHMENTS) {
        event.target.value = '';
        return;
      }

      if (file.size > MAX_ATTACHMENT_FILE_BYTES) {
        toast.error(tVehicle('attachmentMaxSizeExceeded'));
        event.target.value = '';
        return;
      }

      await uploadFile(file, serviceEventId);
      event.target.value = '';
      await onRefetch();
    },
    [attachments.length, uploadFile, serviceEventId, onRefetch, tVehicle]
  );

  const handleDelete = useCallback(async () => {
    if (!attachmentToDelete) return;
    await deleteServiceEventAttachment({
      variables: { id: attachmentToDelete.id },
    });
    toast.success(tVehicle('attachmentsDeleteSuccess'));
    setAttachmentToDelete(null);
    await onRefetch();
  }, [attachmentToDelete, deleteServiceEventAttachment, onRefetch, tVehicle]);

  const handleOpen = useCallback(
    async (attachment: ServiceEventAttachmentRow) => {
      if (!attachment.url) {
        return;
      }
      const token = localStorage.getItem(authConfig.tokenStorageKey);
      if (!token) {
        toast.error(tVehicle('attachmentsOpenNotAuthorized'));
        return;
      }

      const loadingToastId = toast.loading(tVehicle('attachmentsOpening'));
      try {
        const response = await axios.get(`${uploadConfig.api}/${attachment.url}`, {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const blobUrl = URL.createObjectURL(response.data);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        toast.dismiss(loadingToastId);
      } catch (e) {
        toast.dismiss(loadingToastId);
        if (isAxiosError(e) && e.response && (e.response.status === 401 || e.response.status === 403)) {
          toast.error(tVehicle('attachmentsOpenForbidden'));
        } else {
          toast.error(tVehicle('attachmentsOpenError'));
        }
      }
    },
    [tVehicle]
  );

  if (!isOpen) return null;

  return (
    <div style={{ marginTop: '0px' }} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">{tVehicle('attachmentsTitle')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            ✕
          </button>
        </div>
        {showAddAttachmentControls && isImageLoading && (
          <div className="flex items-center gap-2 mb-4 py-2 px-3 bg-primary/10 border border-primary/30 rounded-md text-primary text-sm font-medium">
            <svg
              className="animate-spin h-5 w-5 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>{tVehicle('attachmentsUploading')}</span>
          </div>
        )}
        <p className="text-sm text-gray-color mb-4">
          {tShow('serviceEventAttachmentsInfo')}
        </p>
        <p className="text-sm text-gray-color mb-4">
          {tVehicle('attachmentsMaxInfo')}
        </p>
        <div className="max-h-80 overflow-y-auto border border-gray-200 rounded-md mb-4">
          {attachments.length === 0 ? (
            <p className="p-4 text-sm text-gray-color">{tShow('serviceEventAttachmentsEmpty')}</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    {tVehicle('attachmentsName')}
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-gray-700">
                    {tVehicle('attachmentsCreatedAt')}
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-gray-700">
                    {tVehicle('attachmentsActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {attachments.map((attachment) => (
                  <tr key={attachment.id}>
                    <td className="px-4 py-2 text-gray-900 break-all">{attachment.name}</td>
                    <td className="px-4 py-2 text-gray-color">
                      {formatDate(attachment.createdAt)}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      {attachment.url || attachment.canDelete ? (
                        <>
                          {attachment.url ? (
                            <button
                              type="button"
                              onClick={() => handleOpen(attachment)}
                              className="inline-flex items-center px-3 py-1 border border-primary rounded-md text-xs font-medium text-primary hover:bg-secondary hover:text-white"
                            >
                              {tVehicle('attachmentsOpen')}
                            </button>
                          ) : null}
                          {attachment.canDelete ? (
                            <button
                              type="button"
                              disabled={!hasActiveLicense}
                              onClick={() => setAttachmentToDelete(attachment)}
                              className="inline-flex items-center px-3 py-1 border border-red-600 rounded-md text-xs font-medium text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50"
                            >
                              {tVehicle('attachmentsDelete')}
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div
          className={`flex items-center gap-4 flex-wrap mb-4 ${
            showAddAttachmentControls ? 'justify-between' : 'justify-end'
          }`}
        >
          {showAddAttachmentControls ? (
            <div className="flex flex-col gap-2 max-w-md">
              {atMaxAttachments && (
                <p
                  id="service-event-attachments-max-reached"
                  className="text-sm font-medium text-amber-800"
                  role="status"
                >
                  {tVehicle('attachmentsMaxReached')}
                </p>
              )}
              <label
                className={`inline-flex items-center gap-2 px-4 py-2 border rounded-md text-sm font-medium bg-white ${
                  addAttachmentDisabled
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed opacity-70'
                    : 'border-primary text-primary hover:bg-secondary hover:text-white cursor-pointer'
                }`}
                aria-disabled={addAttachmentDisabled}
                aria-describedby={
                  atMaxAttachments ? 'service-event-attachments-max-reached' : undefined
                }
                title={
                  atMaxAttachments && !isImageLoading
                    ? tVehicle('attachmentsMaxReached')
                    : undefined
                }
              >
                {isImageLoading ? (
                  <svg
                    className="animate-spin h-5 w-5 flex-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 flex-shrink-0"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
                  </svg>
                )}
                <span>{tVehicle('attachmentsAddNew')}</span>
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  disabled={addAttachmentDisabled}
                  onChange={handleFileChange}
                />
              </label>
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            {tVehicle('attachmentsClose')}
          </button>
        </div>

        <ConfirmModal
          isOpen={!!attachmentToDelete}
          onCancel={() => setAttachmentToDelete(null)}
          onConfirm={handleDelete}
          title={tVehicle('attachmentsDeleteConfirmTitle')}
          description={tVehicle('attachmentsDeleteConfirmDescription')}
          confirmLabel={tVehicle('attachmentsDeleteConfirm')}
          cancelLabel={tVehicle('attachmentsDeleteCancel')}
          variant="danger"
        />
      </div>
    </div>
  );
};
